/**
 * AI FAQ API Mixin
 *
 * Handles communication with the AI FAQ backend service
 * Provides methods for asking questions and handling responses
 */

import axios from 'axios'

export default {
    data() {
        return {
            // API configuration
            apiConfig: {
                timeout: 30000, // 30 seconds
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            }
        }
    },

    methods: {
        /**
         * Ask a question to the AI FAQ service
         *
         * @param {string} question - The user's question
         * @param {number|string} occasionId - The occasion ID for context
         * @param {number|string} participantId - The participant ID for context
         * @param {Array} conversationHistory - Prior turns in this session, oldest first, each {question, answer}
         * @param {string|null} expectedFieldType - Set only when this message answers a structured
         *   field the agent requested (EVT-2460), e.g. 'email'. Omitted/null for a normal typed message.
         * @returns {Promise} API response
         */
        async askQuestion(question, occasionId, participantId, conversationHistory = [], expectedFieldType = null) {
            const endpoint = this.apiEndpoint || '/api/v0/ai/ask-question'

            try {
                const response = await axios.post(endpoint, {
                    question: question.trim(),
                    occasion_id: parseInt(occasionId),
                    participant_id: parseInt(participantId),
                    chat_session_identifier: this.getChatSessionIdentifier(occasionId, participantId),
                    conversation_history: conversationHistory,
                    expected_field_type: expectedFieldType
                }, {
                    ...this.apiConfig,
                    // Add CSRF token if available (CakePHP pattern)
                    headers: {
                        ...this.apiConfig.headers,
                        'X-CSRF-Token': this.getCsrfToken()
                    }
                })

                // Handle CakeDC API response format: {status: "success", data: {...}}
                let actualData = response.data
                if (response.data && response.data.status === 'success' && response.data.data) {
                    actualData = response.data.data
                }

                if (actualData && actualData.success) {
                    return actualData
                } else {
                    const errorMsg = actualData && actualData.error ? actualData.error : 'Unknown error occurred'
                    throw new Error(errorMsg)
                }

            } catch (error) {
                // Enhanced error handling
                if (error.response) {
                    // Server responded with error status
                    const errorData = error.response.data
                    const enhancedError = new Error(errorData.error || 'Server error')
                    enhancedError.response = error.response
                    enhancedError.status = error.response.status
                    enhancedError.data = errorData
                    throw enhancedError
                } else if (error.request) {
                    // Network error
                    const networkError = new Error('Network error - please check your connection')
                    networkError.code = 'NETWORK_ERROR'
                    networkError.originalError = error
                    throw networkError
                } else {
                    // Other error
                    throw error
                }
            }
        },

        /**
         * Get CSRF token for CakePHP compatibility
         *
         * @returns {string|null} CSRF token
         */
        getCsrfToken() {
            // Try to get CSRF token from meta tag (common CakePHP pattern)
            const csrfMeta = document.querySelector('meta[name="csrf-token"]')
            if (csrfMeta) {
                return csrfMeta.getAttribute('content')
            }

            // Try to get from cookie (alternative pattern)
            const csrfCookie = this.getCookie('csrfToken')
            if (csrfCookie) {
                return csrfCookie
            }

            // Try to get from window global (if set by template)
            if (window.csrfToken) {
                return window.csrfToken
            }

            return null
        },

        /**
         * Get cookie value by name
         *
         * @param {string} name - Cookie name
         * @returns {string|null} Cookie value
         */
        getCookie(name) {
            const value = `; ${document.cookie}`
            const parts = value.split(`; ${name}=`)
            if (parts.length === 2) {
                return parts.pop().split(';').shift()
            }
            return null
        },

        /**
         * Storage key for a chat session identifier, keyed the same way the customer's own visible
         * transcript is (CustomerServiceAgent's getStorageKey) so the two agree on what a session is
         * (EVT-2566).
         *
         * Previously this was a single '_cts' value: one per browser, shared across every occasion
         * and every identity, and never expiring. The customer saw one thread per occasion per
         * identity, reset after a day; the admin inbox groups on this identifier, so the organiser
         * saw one row per browser containing every conversation that person had ever had with the
         * tenant. Three grains disagreeing.
         *
         * @param {number|string} occasionId - The occasion being asked about
         * @param {number|string|null} participantId - The participant, when the chat is participant-scoped
         * @returns {string} localStorage key
         */
        getChatSessionStorageKey(occasionId, participantId) {
            // Fails loudly rather than interpolating undefined. The occasion is what makes a session
            // a session here, so a missing one would produce `_cts_undefined_guest` — a
            // plausible-looking key under which every occasion-less conversation across the whole
            // site would share one session, silently recreating the bug this keying fixed.
            //
            // Unreachable today: occasionId is a required prop on Messenger, CustomerServiceAgent
            // and FaqAgent, and the API rejects a request without a numeric occasion_id anyway. It
            // exists so that if the widget is ever mounted somewhere without an occasion — the
            // setting is described to organisers as a widget for their whole site — it breaks in
            // front of whoever is building it rather than quietly merging strangers' conversations
            // in production.
            if (occasionId === null || occasionId === undefined || occasionId === '') {
                throw new Error(
                    'AI chat: occasionId is required to derive a chat session identifier. A chat with ' +
                    'no occasion needs a deliberate session scope, not a default one.'
                )
            }

            return `_cts_${occasionId}_${participantId ? participantId : 'guest'}`
        },

        /**
         * How long a session may sit idle before the next message starts a new one.
         *
         * Mirrors the customer's own transcript TTL: CustomerServiceAgent rewrites its stored
         * timestamp on every response (a deep watcher on `responses`), so its expiry is measured
         * from last activity rather than from when the conversation began. Reading its
         * historyExpiryHours prop where present keeps the two clocks identical rather than merely
         * similar; FaqAgent has no such prop and falls back to the same default.
         *
         * @returns {number} Idle milliseconds after which a new session begins
         */
        getChatSessionExpiryMs() {
            // Tested for type rather than truthiness so an explicit 0 is honoured. The transcript
            // treats 0 as "always start fresh"; falling back to 24 here would leave the session
            // outliving the history it is supposed to match, which is the whole bug being fixed.
            const hours = typeof this.historyExpiryHours === 'number' ? this.historyExpiryHours : 24

            return hours * 60 * 60 * 1000
        },

        /**
         * Get or create the chat session identifier for this occasion and identity.
         *
         * Rolling expiry, not absolute: the stored timestamp is refreshed on every call, so an
         * active conversation is never split part-way through — only one left idle past the window
         * rolls over to a new session, at which point the customer can no longer see the old
         * transcript either.
         *
         * @param {number|string} occasionId - The occasion being asked about
         * @param {number|string|null} participantId - The participant, when the chat is participant-scoped
         * @returns {string} Chat session identifier (UUID)
         */
        getChatSessionIdentifier(occasionId, participantId) {
            this.removeLegacyChatSessionKey()

            const storageKey = this.getChatSessionStorageKey(occasionId, participantId)
            const now = Date.now()
            let sessionId = null

            try {
                const stored = JSON.parse(localStorage.getItem(storageKey))

                if (stored && stored.id && now - (stored.ts || 0) < this.getChatSessionExpiryMs()) {
                    sessionId = stored.id
                }
            } catch (e) {
                // Corrupt or legacy value — fall through and start a new session rather than
                // throwing on the send path.
                sessionId = null
            }

            if (!sessionId) {
                sessionId = this.generateUUID()
            }

            try {
                localStorage.setItem(storageKey, JSON.stringify({ id: sessionId, ts: now }))
            } catch (e) {
                // Storage full or blocked (private browsing). The identifier is still returned so
                // this turn is grouped correctly; only continuity across page loads is lost.
                console.warn('Failed to persist chat session identifier:', e)
            }

            return sessionId
        },

        /**
         * Delete the pre-EVT-2566 '_cts' key, which nothing reads or writes any more.
         *
         * Originally left in place so a revert would resume the previous session rather than
         * stranding it. That was the wrong trade: because it is never touched, it sits in
         * localStorage frozen at its old value forever, and it shares a prefix with the live keys —
         * so the first thing anyone inspecting localStorage finds is a session identifier that never
         * changes no matter what they do. It cost real time during UAT of this very change. A revert
         * simply starting a fresh session is harmless by comparison.
         *
         * @returns {void}
         */
        removeLegacyChatSessionKey() {
            try {
                localStorage.removeItem('_cts')
            } catch (e) {
                // Nothing to recover from — the key is dead either way.
            }
        },

        /**
         * End the current session so the next message starts a new one.
         *
         * Called when the customer clears their chat. Without this, clearing was display-only: they
         * saw an empty thread while every following message still grouped into the same inbox row as
         * everything before it.
         *
         * @param {number|string} occasionId - The occasion being asked about
         * @param {number|string|null} participantId - The participant, when the chat is participant-scoped
         * @returns {void}
         */
        resetChatSession(occasionId, participantId) {
            try {
                localStorage.removeItem(this.getChatSessionStorageKey(occasionId, participantId))
            } catch (e) {
                console.warn('Failed to reset chat session:', e)
            }
        },

        /**
         * Generate a UUID v4
         *
         * @returns {string} UUID
         */
        generateUUID() {
            if (typeof crypto !== 'undefined' && crypto.randomUUID) {
                return crypto.randomUUID()
            }

            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                const r = Math.random() * 16 | 0
                const v = c === 'x' ? r : (r & 0x3 | 0x8)
                return v.toString(16)
            })
        },

        /**
         * Validate question before sending to API
         *
         * @param {string} question - The question to validate
         * @returns {object} Validation result
         */
        validateQuestionForApi(question) {
            const trimmed = question.trim()

            if (!trimmed) {
                return {
                    valid: false,
                    error: 'Question cannot be empty'
                }
            }

            if (trimmed.length > 500) {
                return {
                    valid: false,
                    error: 'Question must be no more than 500 characters long'
                }
            }

            // Check for suspicious patterns (basic XSS prevention)
            if (/<[^>]*>/.test(trimmed)) {
                return {
                    valid: false,
                    error: 'HTML tags are not allowed in questions'
                }
            }

            // Check for script injection patterns
            if (/javascript:|script:|eval\(|setTimeout\(|setInterval\(/i.test(trimmed)) {
                return {
                    valid: false,
                    error: 'Invalid characters detected in question'
                }
            }

            return {
                valid: true,
                question: trimmed
            }
        },

        /**
         * Format API error for user display
         *
         * @param {Error} error - The error object
         * @returns {object} Formatted error info
         */
        formatApiError(error) {
            let message = 'An unexpected error occurred'
            let type = 'general'
            let canRetry = true

            if (error.response) {
                const status = error.response.status
                const data = error.response.data

                switch (status) {
                    case 400:
                        message = data.error || 'Invalid request'
                        type = 'validation'
                        canRetry = false
                        break
                    case 404:
                        message = 'Event not found'
                        type = 'not-found'
                        canRetry = false
                        break
                    case 429:
                        message = 'Too many requests. Please wait before asking another question.'
                        type = 'rate-limit'
                        canRetry = false
                        break
                    case 500:
                        message = 'Server error. Please try again later.'
                        type = 'server'
                        canRetry = true
                        break
                    default:
                        message = data.error || `Server error (${status})`
                        type = 'server'
                        canRetry = true
                }
            } else if (error.code === 'NETWORK_ERROR') {
                message = 'Network error. Please check your connection.'
                type = 'network'
                canRetry = true
            }

            return {
                message,
                type,
                canRetry,
                originalError: error
            }
        },

        /**
         * Log API usage for debugging (development only)
         *
         * @param {string} action - The action being performed
         * @param {object} data - Additional data to log
         */
        logApiUsage(action, data = {}) {
            // API usage logging removed for production
        },

        /**
         * Setup axios interceptors for common functionality
         * Call this in component mounted() if needed
         */
        setupApiInterceptors() {
            // Request interceptor
            axios.interceptors.request.use(
                (config) => {
                    this.logApiUsage('Request', {
                        url: config.url,
                        method: config.method
                    })
                    return config
                },
                (error) => {
                    this.logApiUsage('Request Error', { error: error.message })
                    return Promise.reject(error)
                }
            )

            // Response interceptor
            axios.interceptors.response.use(
                (response) => {
                    this.logApiUsage('Response', {
                        status: response.status,
                        cached: response.data && response.data.cached
                    })
                    return response
                },
                (error) => {
                    this.logApiUsage('Response Error', {
                        status: error.response && error.response.status,
                        message: error.message
                    })
                    return Promise.reject(error)
                }
            )
        }
    }
}



// WEBPACK FOOTER //
// ./src/Assets/components/AiAgents/mixins/aiFaqApi.js